import { isAxiosError } from "@/api/api";
import { toFormData } from "@/fileUpload/fileUploadDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { isObject } from "lodash";
import {
  HttpEvent,
  HttpEventCallbacks,
  httpDelete,
  httpGet,
  httpPost,
} from "@/events/eventsDomain";
import { toQueryString } from "@/utils/queryParams";
import { IssuedBy } from "@/types/const/Transactions";

export type OwnershipSource = "DCOMPANY_REGISTER" | "TRANK";

export type HoldingCompany = {
  organisationNumber: string;
  name: string;
  shareholderId?: number;
  source: OwnershipSource;
  asOf?: string;
};

export type HolderHoldingCompanies = {
  holderId: string;
  holdingCompanies: HoldingCompany[];
};

export type ExerciseReceiver =
  | { type: "HOLDER" }
  | { type: "HOLDING_COMPANY"; organisationNumber: string };

export type ExerciseSpec = {
  exerciseId: string;
  grantId: string;
  trancheId: string;
  count: number;
  shareTypeId?: number;
  receiver: ExerciseReceiver;
  isEmployee: boolean;
  requestId?: string;
};

export type ExerciseShareIssue = {
  registrationDate?: string;
  shareholderRightsDate?: string;
  issuedBy?: IssuedBy;
  boardMeetingPoDDate?: string | null;
  generalMeetingDate?: string | null;
};

export type ExerciseOptionsCommand = {
  commandType: "EXERCISE_OPTIONS";
  exerciseDate: string;
  exercises: ExerciseSpec[];
  remarks?: string;
  attachmentReference: string;
  issue?: ExerciseShareIssue;
};

export type ExerciseOptionsResult = { transactionLogId?: number };

export type ReverseExerciseCommand = {
  commandType: "REVERSE_EXERCISE";
  exerciseId: string;
  remarks?: string;
};

export type RequestExerciseCommand = {
  commandType: "REQUEST_EXERCISE";
  grantId: string;
  trancheId: string;
  requestDate: string;
  count: number;
};

export type EditExerciseRequestCommand = {
  commandType: "EDIT_EXERCISE_REQUEST";
  grantId: string;
  trancheId: string;
  requestId: string;
  requestDate: string;
  count: number;
};

export type ExerciseRequestNotify = {
  language: string;
  copyTo?: string;
  copyToAdministrators: boolean;
};

export type UpdateExerciseRequestCommand = {
  commandType: "UPDATE_EXERCISE_REQUEST";
  grantId: string;
  trancheId: string;
  requestId: string;
  status: "ACKNOWLEDGED" | "REJECTED" | "DELETED";
  remarks?: string;
  notify?: ExerciseRequestNotify;
};

export type InstrumentCommandType =
  | "REGISTER_HOLDER"
  | "UPDATE_HOLDER"
  | "CREATE_PLAN"
  | "UPDATE_PLAN"
  | "UPDATE_PLAN_VESTING"
  | "REGISTER_GRANT"
  | "UPDATE_GRANT"
  | "UPDATE_VESTING"
  | "CANCEL_GRANT"
  | "ACCELERATE_GRANT"
  | "DELETE_GRANT"
  | "EXERCISE_OPTIONS"
  | "REVERSE_EXERCISE"
  | "REQUEST_EXERCISE"
  | "EDIT_EXERCISE_REQUEST"
  | "UPDATE_EXERCISE_REQUEST";

export type InstrumentCommand = { commandType: InstrumentCommandType; [field: string]: unknown };

const backendErrorBody = (err: unknown): { error?: string; message?: string } => {
  if (!isAxiosError(err)) {
    return {};
  }
  const data: unknown = err.response?.data;
  if (!isObject(data)) {
    return {};
  }
  return {
    error: "error" in data && typeof data.error === "string" ? data.error : undefined,
    message: "message" in data && typeof data.message === "string" ? data.message : undefined,
  };
};

// Domain errors the frontend knows how to phrase in the user's language;
// anything else falls back to the backend's English message.
const knownCommandErrors: Partial<Record<string, TMessageKey>> = {
  DuplicateVestingDates: "options.duplicate-vesting-dates",
  ExerciseAboveExercisable: "options.exercise-error-above-exercisable",
  ExerciseHasShares: "options.reverse-error-has-shares",
  NotHoldingCompany: "options.exercise-error-not-holding-company",
  PlanExpirationNotAfterPoolDate: "options.expiration-after-pool-date",
  ExerciseWindowEndsBeforeStart: "options.exercise-window-ends-before-start",
  OverlappingExerciseWindows: "options.exercise-windows-overlap",
  StartedExerciseWindowChanged: "options.exercise-window-started",
  ExerciseWindowBeforeProgramStart: "options.exercise-window-before-program-start",
  ExerciseWindowAfterExpiration: "options.exercise-window-after-expiration",
  ExerciseRequestsNotAllowed: "options.exercise-requests-not-allowed",
  ExerciseRequestOutsideWindow: "options.exercise-request-outside-window",
  ExerciseWindowRequired: "options.exercise-window-required",
};

export const commandFailureMessage = (err: unknown): TMessage => {
  const { error, message } = backendErrorBody(err);
  const knownKey = knownCommandErrors[error ?? ""];
  if (knownKey !== undefined) {
    return { key: knownKey };
  }
  return message === undefined ? { key: "general-error" } : { translated: message };
};

export type InstrumentHolderApiClient = {
  getHoldingCompanies: (
    orgNo: string,
    query: { planId?: string; holderIds?: string[] },
    params: HttpEventCallbacks<HolderHoldingCompanies[]>,
  ) => HttpEvent<HolderHoldingCompanies[]>;
};

export const instrumentHolderApiClient: InstrumentHolderApiClient = {
  getHoldingCompanies: (orgNo, { planId, holderIds }, params) =>
    httpGet({
      url: `/instruments/${orgNo}/holding-companies${toQueryString({
        planId,
        holderId: holderIds?.join(","),
      })}`,
      ...params,
    }),
};

export type InstrumentCommandApiClient = {
  send: <T = unknown>(
    orgNo: string,
    command: InstrumentCommand,
    params: HttpEventCallbacks<T>,
  ) => HttpEvent<T>;
};

export const instrumentCommandApiClient: InstrumentCommandApiClient = {
  send: (orgNo, command, params) =>
    httpPost({ url: `/instruments/${orgNo}/commands`, data: command, ...params }),
};

export type OptionApiClient = {
  uploadPlanAttachments: (
    orgNo: string,
    planId: string,
    files: File[],
    params: HttpEventCallbacks<unknown>,
  ) => HttpEvent<unknown>;
  deletePlanAttachment: (
    orgNo: string,
    planId: string,
    attachmentId: string,
    params: HttpEventCallbacks<unknown>,
  ) => HttpEvent<unknown>;
  uploadExerciseAttachments: (
    orgNo: string,
    reference: string,
    files: File[],
    params: HttpEventCallbacks<unknown>,
  ) => HttpEvent<unknown>;
};

export const optionApiClient: OptionApiClient = {
  uploadPlanAttachments: (orgNo, planId, files, params) =>
    httpPost({
      url: `/instruments/${orgNo}/options/plans/${planId}/attachments`,
      data: toFormData(files),
      ...params,
    }),
  deletePlanAttachment: (orgNo, planId, attachmentId, params) =>
    httpDelete({
      url: `/instruments/${orgNo}/options/plans/${planId}/attachments/${attachmentId}`,
      ...params,
    }),
  uploadExerciseAttachments: (orgNo, reference, files, params) =>
    httpPost({
      url: `/instruments/${orgNo}/options/exercises/${reference}/attachments`,
      data: toFormData(files),
      ...params,
    }),
};
